<template>
  <div :class="{ 'card-container': hasProducts }">
    <VueDraggableNext
      v-if="hasProducts"
      :model-value="store.addedProductList"
      class="flex flex-col items-start self-stretch"
      handle=".drag-handle"
      @change="(e) => onProductSort(e, false)"
    >
      <div
        v-for="(product, index) in store.addedProductList"
        :key="product.productId || index"
        class="flex flex-col items-start self-stretch"
      >
        <ProductCard
          :product="product"
          :product-index="index"
          :is-bum-product="false"
          @product:deleted="(idx) => onProductDelete(idx, false)"
          @product:settings="(idx) => onProductSettings(idx, false)"
        >
          <template #handle>
            <div class="drag-handle cursor-pointer" role="button" tabindex="0">
              <Menu05Icon class="h-4 w-4" aria-hidden="true" />
            </div>
          </template>
        </ProductCard>
        <div v-if="index !== store.addedProductList.length - 1" class="divider" />
      </div>
    </VueDraggableNext>
    <div v-if="hasBumpProducts" class="divider"></div>
    <VueDraggableNext
      v-if="hasBumpProducts"
      :model-value="store.bumProductList"
      class="flex flex-col items-start self-stretch"
      handle=".drag-handle"
      @change="(e) => onProductSort(e, true)"
    >
      <div
        v-for="(product, index) in store.bumProductList"
        :key="product.productId || index"
        class="flex flex-col items-start self-stretch"
      >
        <ProductCard
          :product="product"
          :product-index="index"
          :is-bum-product="true"
          @product:deleted="(idx) => onProductDelete(idx, true)"
          @product:settings="(idx) => onProductSettings(idx, true)"
        >
          <template #handle>
            <div class="drag-handle cursor-pointer" role="button" tabindex="0">
              <Menu05Icon class="h-4 w-4" aria-hidden="true" />
            </div>
          </template>
        </ProductCard>
        <div v-if="index !== store.bumProductList.length - 1" class="divider" />
      </div>
    </VueDraggableNext>
  </div>
</template>

<script setup lang="ts">
import { Menu05Icon } from "@gohighlevel/ghl-icons/24/outline";
import { computed } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useAppStore } from "../../../store/app";
import type { BumProduct, Product } from "../../../types/product";
import ProductCard from "./ProductCard.vue";

interface SortEvent {
  moved?: {
    oldIndex: number;
    newIndex: number;
    element: Product | BumProduct;
  };
}

const store = useAppStore();

const emits = defineEmits(["product:settings"]);

const hasProducts = computed(() => store.addedProductList.length > 0);
const hasBumpProducts = computed(() => store.bumProductList.length > 0);

const onProductDelete = (index: number, isBumpProduct: boolean): void => {
  try {
    if (isBumpProduct) {
      store.bumProductList.splice(index, 1);
    } else {
      store.addedProductList.splice(index, 1);
    }
    if (store.bumProductList.length <= 0) {
      store.payment.bumpSettings.enableBump = false;
    }
    store.addToHistory();
    store.anyUnsavedChanges = true;
  } catch (error) {
    console.error("Error deleting product:", error);
  }
};

const onProductSort = (sorted: SortEvent, isBumpProduct: boolean): void => {
  if (sorted.moved) {
    try {
      const { oldIndex, newIndex } = sorted.moved;
      if (isBumpProduct) {
        const oldProduct = store.bumProductList[oldIndex];
        store.bumProductList[oldIndex] = store.bumProductList[newIndex];
        store.bumProductList[newIndex] = oldProduct;
      } else {
        const oldProduct = store.addedProductList[oldIndex];
        store.addedProductList[oldIndex] = store.addedProductList[newIndex];
        store.addedProductList[newIndex] = oldProduct;
      }

      store.addToHistory();
      store.anyUnsavedChanges = true;
    } catch (error) {
      console.error("Error sorting products:", error);
    }
  }
};

const onProductSettings = (index: number, isBumpProduct: boolean): void => {
  emits("product:settings", index, isBumpProduct);
};
</script>

<style scoped>
.card-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 8px;
  border: 0.5px solid #d0d5dd;
}
.card-container-bump {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-top-left-radius: 0px;
  border-top-right-radius: 0px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  border: 0.5px solid #d0d5dd;
}

.divider {
  width: 100%;
  border-bottom: 1px solid #d0d5dd;
}

.drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  cursor: grab;
  transition: all 0.2s ease;
}

.drag-handle:hover {
  background-color: #f3f4f6;
}

.drag-handle:focus {
  outline: 2px solid #155eef;
  outline-offset: 2px;
}

.drag-handle:active {
  cursor: grabbing;
  background-color: #e5e7eb;
}

@media (hover: hover) {
  .drag-handle:hover {
    background-color: #f3f4f6;
  }
}

@media (prefers-reduced-motion: reduce) {
  .drag-handle {
    transition: none;
  }
}
</style>
